import {
  Badge,
  Box,
  Divider,
  Flex,
  Heading,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  PlatformRequirementDocumentVerdict,
  PlatformRequirementIssueStatus,
} from '@client/graphql/__generated__/types';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { FC, MouseEvent, ReactElement, ReactNode, useId } from 'react';
import { match } from 'ts-pattern';
import { formatAttemptTimestamp as formatChecklistTimestamp } from './autopilotAttempt.format';
import {
  ChecklistContextDocumentView,
  ChecklistContractDocumentView,
  ChecklistEvaluationDocumentView,
  ChecklistEvaluationStatus,
  ChecklistGroupedEvaluationDocumentView,
  ChecklistIssueView,
  DOCUMENT_BADGE_TONE_STYLE,
  DocumentBadgeTone,
} from './checklistIssue.types';
import {
  ChecklistCategoryChip,
  ChecklistFormChip,
  ChecklistRequirementLine,
} from './checklistIssueChips';
import { ChecklistGroup, groupChecklistIssues } from './checklistIssueGrouping';
import { ChecklistIssueTooltipContent } from './ChecklistIssueTooltipContent';
import { formatPlatformRequirementIssueStatus } from './platformRequirementIssueStatus.format';
import { formatPlatformRequirementProblemType } from './platformRequirementProblemType.format';

export type {
  ChecklistContextDocumentView,
  ChecklistContractDocumentView,
  ChecklistEvaluationDocumentView,
  ChecklistEvaluationStatus,
  ChecklistGroupedEvaluationDocumentView,
  ChecklistIssueView,
  DocumentBadgeTone,
};

/**
 * How each checklist group is drawn.
 *
 * The collapsed variants deliberately differ: a checklist issue should read
 * like the validation rows it sits beside, and those rows are shaped
 * differently on each surface. The inline evidence behind them is identical.
 *
 * - `expanded`: the internal debug drawer's full evaluation card.
 * - `compact`: extension side panel / embedded drawer. One line, mirroring
 *   {@link BaseValidationItem}.
 * - `suggestion`: agent compliance review page. Two lines inside the
 *   suggestions card, mirroring `DocumentSuggestionsList`.
 */
export type ChecklistIssueRowVariant = 'expanded' | 'compact' | 'suggestion';

/**
 * Agent-only actions. Provided by the agent compliance review surface.
 *
 * Both callbacks take the whole group's issues rather than one issue at a time:
 * the affordances are group-scoped, and the caller prompts for a single ignore
 * reason covering the group. Collecting that reason needs a modal, which belongs
 * on the agent surface — this component is also rendered by the internal app, so
 * it must not instantiate one itself.
 */
interface ChecklistIssueActions {
  /**
   * The agent asked to ignore the group's open issues. Reporting the intent
   * rather than performing it is deliberate: the caller prompts for the reason
   * and owns the write, so this component never has to know about either.
   */
  onIgnore: (issues: ChecklistIssueView[]) => void;
  /** Un-ignores every agent-ignored issue in the group. */
  onUndoIgnore: (issues: ChecklistIssueView[]) => void;
  isIgnoring: boolean;
  /** The exact action and public issues currently being mutated. */
  pendingAction?: {
    action: 'ignore' | 'undo';
    publicIssueIds: string[];
  } | null;
}

type ChecklistIssuesListProps = {
  issues: ChecklistIssueView[];
  selectedIngestionDocumentId?: string;
  selectedPlatformDocumentId?: string | null;
  /**
   * Evaluation state for the selected checklist item. Distinguishes an honest
   * "no data" state from a positive clean state and from a failed evaluation.
   */
  evaluationStatus: ChecklistEvaluationStatus;
  /** How each group is drawn. Defaults to the internal expanded card. */
  rowVariant?: ChecklistIssueRowVariant;
  /** Selected checklist item name, shown in the no-issue empty state. */
  checklistItemName?: string | null;
  /**
   * The selected checklist item's allowed categories/forms, shown as the
   * "Needs at least one attachment classified with:" box in the no-issue empty
   * state. Known on the live surface; absent on the persisted no-data surface.
   */
  requiredDocumentCategories?: string[];
  requiredForms?: string[];
  /** Attached docs shown when there is no current issue for the selected item. */
  contextDocuments?: ChecklistContextDocumentView[];
  /**
   * Internal-only: show the "Evaluated <timestamp>" line beneath each checklist
   * item. External surfaces never show it because the persisted history is
   * incomplete and the timestamp is not agent-facing.
   */
  showEvaluatedAt?: boolean;
  /**
   * Labels a fully-handled checklist item with who handled it, matching the chip
   * validation suggestions already carry. Resubmitted agent issues retain the
   * "Agent ignored" attribution. Both reviewer and agent-review surfaces opt in
   * so every crossed-out row explains why it was handled.
   */
  showStatusChip?: boolean;
  onOpenDocument?: (document: ChecklistEvaluationDocumentView) => void;
} & ({ actions?: never } | { actions: ChecklistIssueActions });

/**
 * Resolves the status badge for one attached document. "No mismatch" (success)
 * is only shown when there is positive evidence — a live `MATCHED` verdict or a
 * completed `evaluated` state. Without evidence we show a neutral "No data"
 * rather than falsely asserting the attachment is clean (BLO-13160).
 */
const getDocumentStatusBadge = (input: {
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  evaluationStatus: ChecklistEvaluationStatus;
}): { label: string; tone: DocumentBadgeTone } =>
  match(input)
    .with({ verdict: PlatformRequirementDocumentVerdict.MISMATCHED }, () => ({
      label: 'Mismatch',
      tone: 'error' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.MATCHED }, () => ({
      label: 'No mismatch',
      tone: 'success' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.UNRESOLVED }, () => ({
      label: 'Unresolved',
      tone: 'neutral' as const,
    }))
    .with({ verdict: PlatformRequirementDocumentVerdict.UNCONFIGURED }, () => ({
      label: 'No requirement',
      tone: 'neutral' as const,
    }))
    .with({ isProblemDocument: true }, () => ({
      label: 'Mismatch',
      tone: 'error' as const,
    }))
    .with({ evaluationStatus: 'evaluated' }, () => ({
      label: 'No mismatch',
      tone: 'success' as const,
    }))
    .otherwise(() => ({ label: 'No data', tone: 'neutral' as const }));

const DocumentStatusBadge: FC<{
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  evaluationStatus: ChecklistEvaluationStatus;
}> = ({ isProblemDocument, verdict, evaluationStatus }) => {
  const { label, tone } = getDocumentStatusBadge({
    isProblemDocument,
    verdict,
    evaluationStatus,
  });
  const style = DOCUMENT_BADGE_TONE_STYLE[tone];

  return (
    <Badge
      bg={style.bg}
      border={style.border ? '1px solid' : undefined}
      borderColor={style.border}
      borderRadius="full"
      color={style.color}
    >
      {label}
    </Badge>
  );
};

interface ChecklistGroupLifecycleSummary {
  displayStatus: PlatformRequirementIssueStatus | null;
}

const CHECKLIST_ISSUE_STATUS_PRIORITY: Record<
  PlatformRequirementIssueStatus,
  number
> = {
  [PlatformRequirementIssueStatus.OPEN]: 3,
  [PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER]: 2,
  [PlatformRequirementIssueStatus.RESUBMITTED]: 1,
  [PlatformRequirementIssueStatus.IGNORED_BY_AGENT]: 0,
};

/**
 * Builds the compact lifecycle summary shown for one checklist group. This is
 * an intentional client-side display policy: any open issue keeps the group
 * open and suppresses the chip; otherwise mixed handled statuses collapse to
 * reviewer ignored, then resubmitted, then agent ignored.
 */
const summarizeGroupLifecycle = (
  issues: ChecklistIssueView[]
): ChecklistGroupLifecycleSummary => {
  const displayStatus = issues.reduce<PlatformRequirementIssueStatus | null>(
    (currentStatus, issue) =>
      currentStatus == null ||
      CHECKLIST_ISSUE_STATUS_PRIORITY[issue.status] >
        CHECKLIST_ISSUE_STATUS_PRIORITY[currentStatus]
        ? issue.status
        : currentStatus,
    null
  );

  return match(displayStatus)
    .with(null, PlatformRequirementIssueStatus.OPEN, () => ({
      displayStatus: null,
    }))
    .with(
      PlatformRequirementIssueStatus.IGNORED_BY_AGENT,
      PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER,
      PlatformRequirementIssueStatus.RESUBMITTED,
      (status) => ({ displayStatus: status })
    )
    .exhaustive();
};

/**
 * Solid "Agent ignored" / "Ignored by reviewer" chip. A resubmitted checklist
 * issue remains labelled "Agent ignored" to match validation errors: the chip
 * describes who handled the issue, while resubmission remains lifecycle state.
 * Kept unstruck and at full contrast (unlike the faded row it sits in) so the
 * viewer can tell at a glance who disposed of the issue — the checklist
 * counterpart to the validation row's chip in {@link BaseValidationItem}.
 */
const formatChecklistIssueStatus = (
  status: PlatformRequirementIssueStatus
): string =>
  match(status)
    .with(
      PlatformRequirementIssueStatus.IGNORED_BY_AGENT,
      PlatformRequirementIssueStatus.RESUBMITTED,
      () => 'Agent ignored'
    )
    .with(
      PlatformRequirementIssueStatus.OPEN,
      PlatformRequirementIssueStatus.IGNORED_BY_REVIEWER,
      formatPlatformRequirementIssueStatus
    )
    .exhaustive();

const ChecklistIssueStatusChip: FC<{
  status: PlatformRequirementIssueStatus;
}> = ({ status }) => (
  <Box
    bg="compliance.tag.bg"
    borderRadius="xs"
    color="compliance.text.secondary"
    flexShrink={0}
    fontSize="xs"
    fontWeight="medium"
    px={1.5}
    py={0.5}
    whiteSpace="nowrap"
  >
    {formatChecklistIssueStatus(status)}
  </Box>
);

const Label: FC<{ children: string; opacity?: number }> = ({
  children,
  opacity,
}) => (
  <Text
    color="compliance.text.muted"
    fontSize="xs"
    fontWeight="medium"
    letterSpacing="wider"
    opacity={opacity}
    textTransform="uppercase"
  >
    {children}
  </Text>
);

/**
 * The "Needs at least one attachment classified with: …" requirement, boxed for
 * the internal expanded card. The collapsed surfaces show the same line inside
 * the hover tooltip, unboxed — hence the shared {@link ChecklistRequirementLine}.
 * Renders nothing when neither allowed categories nor forms are known.
 */
const RequiredClassificationBox: FC<{
  allowedDocumentCategories: string[];
  allowedForms: string[];
}> = ({ allowedDocumentCategories, allowedForms }) => {
  if (allowedDocumentCategories.length === 0 && allowedForms.length === 0) {
    return null;
  }

  return (
    <Box
      bg="compliance.document.bg"
      border="1px solid"
      borderColor="compliance.document.border"
      borderRadius="md"
      p={3}
    >
      <ChecklistRequirementLine
        allowedDocumentCategories={allowedDocumentCategories}
        allowedForms={allowedForms}
      />
    </Box>
  );
};

/**
 * One attached-document card: name, selected highlight, status badge, and the
 * identified contract documents (form/category). Internal expanded card only —
 * the collapsed surfaces render this evidence in the hover tooltip instead. The
 * contract-documents sub-block is shown only when `contractDocuments` is
 * provided (the persisted no-issue surface has no per-document classifications).
 */
const AttachedDocumentCard: FC<{
  documentName: string;
  isProblemDocument: boolean;
  verdict?: PlatformRequirementDocumentVerdict | null;
  contractDocuments?: ChecklistContractDocumentView[] | null;
  isSelected: boolean;
  evaluationStatus: ChecklistEvaluationStatus;
  onOpen?: () => void;
}> = ({
  documentName,
  isProblemDocument,
  verdict,
  contractDocuments,
  isSelected,
  evaluationStatus,
  onOpen,
}) => (
  <Box
    bg={isSelected ? 'compliance.selected.bg' : 'transparent'}
    border="1px solid"
    borderColor={
      isSelected ? 'compliance.selected.border' : 'compliance.document.border'
    }
    borderLeftWidth={isSelected ? '3px' : '1px'}
    borderRadius="md"
    p={3}
  >
    <Text color="compliance.text.primary" fontSize="sm" fontWeight="semibold">
      {documentName}
    </Text>

    <Flex align="center" gap={2} mt={1} wrap="wrap">
      {isSelected ? (
        <Badge
          bg="transparent"
          border="1px solid"
          borderColor="compliance.selected.border"
          borderRadius="full"
          color="compliance.selected.text"
        >
          Selected document
        </Badge>
      ) : null}
      <DocumentStatusBadge
        evaluationStatus={evaluationStatus}
        isProblemDocument={isProblemDocument}
        verdict={verdict}
      />
    </Flex>

    {contractDocuments != null ? (
      <Box mt={2}>
        <Label>{`Contract documents (${contractDocuments.length})`}</Label>
        {contractDocuments.length === 0 ? (
          <Text color="compliance.text.muted" fontSize="xs" fontStyle="italic">
            No contract documents identified
          </Text>
        ) : (
          <VStack align="stretch" mt={1} spacing={1}>
            {contractDocuments.map((contractDocument, index) => (
              <Flex key={`cd-${index}`} align="center" gap={2} wrap="wrap">
                {contractDocument.form ? (
                  <ChecklistFormChip form={contractDocument.form} />
                ) : (
                  <Text
                    color="compliance.text.muted"
                    fontSize="xs"
                    fontStyle="italic"
                  >
                    no form
                  </Text>
                )}
                {contractDocument.documentCategory ? (
                  <ChecklistCategoryChip
                    category={contractDocument.documentCategory}
                  />
                ) : (
                  <Text
                    color="compliance.text.muted"
                    fontSize="xs"
                    fontStyle="italic"
                  >
                    no category
                  </Text>
                )}
              </Flex>
            ))}
          </VStack>
        )}
      </Box>
    ) : null}

    {onOpen ? (
      <Flex
        align="center"
        as="button"
        color="compliance.link.fg"
        fontSize="sm"
        fontWeight="semibold"
        gap={1}
        mt={2}
        type="button"
        onClick={onOpen}
      >
        Open
        <Icon as={ExternalLinkIcon} boxSize="12px" />
      </Flex>
    ) : null}
  </Box>
);

/**
 * Per-group state every variant needs: which issues are actionable, whether the
 * group reads as handled, and the distinct reasons given for handling it.
 */
interface ChecklistGroupState {
  openIssues: ChecklistIssueView[];
  ignoredIssues: ChecklistIssueView[];
  displayStatus: PlatformRequirementIssueStatus | null;
  isResolved: boolean;
  ignoreReasons: string[];
}

const deriveGroupState = (group: ChecklistGroup): ChecklistGroupState => {
  const { displayStatus } = summarizeGroupLifecycle(group.issues);

  return {
    openIssues: group.issues.filter(
      (issue) =>
        issue.isAgentActionable &&
        issue.status === PlatformRequirementIssueStatus.OPEN
    ),
    // Undo stays bound to these: only an agent ignore is reversible here.
    ignoredIssues: group.issues.filter(
      (issue) =>
        issue.isAgentActionable &&
        issue.status === PlatformRequirementIssueStatus.IGNORED_BY_AGENT
    ),
    displayStatus,
    isResolved: displayStatus != null,
    // One reason normally covers the group (a single Ignore applies the same
    // text to every open issue), but a group can hold issues ignored in
    // separate rounds — so collect the distinct ones rather than the first.
    ignoreReasons: [
      ...new Set(
        group.issues
          .map((issue) => issue.ignoreReason?.trim())
          .filter((reason): reason is string => Boolean(reason))
      ),
    ],
  };
};

/**
 * Agent ignore explanation, appended to the checklist evidence.
 * Only meaningful once handled WITH a reason; an open group has nothing to
 * explain.
 */
const IgnoreReasons: FC<{ reasons: string[] }> = ({ reasons }) => (
  <Box>
    <Heading
      color="compliance.tooltip.text"
      mb={1}
      opacity={0.6}
      size="smallUppercase"
    >
      Agent&apos;s reason for ignoring
    </Heading>
    <VStack align="stretch" spacing={1}>
      {reasons.map((reason) => (
        <Text key={reason} color="compliance.tooltip.text" fontSize="sm">
          {reason}
        </Text>
      ))}
    </VStack>
  </Box>
);

/**
 * Adds an explicit inline evidence disclosure to a collapsed checklist row.
 * The native button makes the evidence available to keyboard and touch users,
 * while keeping row actions such as Ignore and Undo as separate controls.
 */
const ChecklistRowDisclosure: FC<{
  group: ChecklistGroup;
  ignoreReasons: string[];
  children: ReactElement;
}> = ({ group, ignoreReasons, children }) => {
  const { isOpen, onToggle } = useDisclosure();
  const evidenceId = useId();
  const hasEvidence = !(
    group.allowedDocumentCategories.length === 0 &&
    group.allowedForms.length === 0 &&
    group.evaluationDocuments.length === 0 &&
    ignoreReasons.length === 0
  );

  if (!hasEvidence) {
    return children;
  }

  const action = isOpen ? 'Hide' : 'View';

  return (
    <VStack align="stretch" spacing={isOpen ? 2 : 0} width="100%">
      <Flex align="center" gap={2} justify="space-between">
        <Box flex={1} minW={0}>
          {children}
        </Box>
        <IconButton
          aria-controls={evidenceId}
          aria-expanded={isOpen}
          aria-label={`${action} evidence for ${group.checklistItemName}`}
          color="compliance.text.muted"
          flexShrink={0}
          icon={<Icon as={isOpen ? ChevronDownIcon : ChevronRightIcon} />}
          size="sm"
          variant="ghost"
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        />
      </Flex>
      {isOpen ? (
        <Box
          aria-label={`Evidence for ${group.checklistItemName}`}
          bg="compliance.tooltip.bg"
          border="1px solid"
          borderColor="compliance.tooltip.border"
          borderRadius="md"
          color="compliance.tooltip.text"
          id={evidenceId}
          p={3}
          role="region"
        >
          <VStack align="stretch" spacing={3}>
            <ChecklistIssueTooltipContent
              allowedDocumentCategories={group.allowedDocumentCategories}
              allowedForms={group.allowedForms}
              checklistItemName={group.checklistItemName}
              evaluationDocuments={group.evaluationDocuments}
            />
            {ignoreReasons.length > 0 ? (
              <>
                <Divider
                  aria-label="Agent reason separator"
                  borderColor="compliance.tooltip.text"
                  opacity={0.2}
                />
                <IgnoreReasons reasons={ignoreReasons} />
              </>
            ) : null}
          </VStack>
        </Box>
      ) : null}
    </VStack>
  );
};

/**
 * Extension side panel / embedded drawer row. One line, matching the validation
 * rows directly above it: warning icon, checklist item, then the short reason.
 *
 * The item name leads because it is the part that varies — every checklist row
 * currently carries the same reason, so reason-first would give a column of
 * identical text to scan past.
 */
const CompactChecklistRow: FC<{
  group: ChecklistGroup;
  state: ChecklistGroupState;
  showStatusChip: boolean;
}> = ({ group, state, showStatusChip }) => {
  const { isResolved, displayStatus, ignoreReasons } = state;

  return (
    <ChecklistRowDisclosure group={group} ignoreReasons={ignoreReasons}>
      <Flex
        _hover={{ bg: 'compliance.hover.bg' }}
        align="center"
        borderRadius="sm"
        gap={2}
        mx={-2}
        px={2}
        py={1}
      >
        <Icon
          as={TriangleAlertIcon}
          color={
            isResolved ? 'compliance.text.muted' : 'compliance.status.error'
          }
          flexShrink={0}
        />
        {/* One text flow, not two flex siblings. The panel is narrow and
            checklist item names are long, so as siblings each would wrap in its
            own column and strand the "-" at the start of a line, where it reads
            as a stray bullet rather than a separator. Nesting the reason mirrors
            how {@link BaseValidationItem} carries its subtitle. */}
        <Text
          color={
            isResolved ? 'compliance.text.muted' : 'compliance.text.primary'
          }
          fontSize="sm"
          fontWeight="semibold"
          textDecoration={isResolved ? 'line-through' : undefined}
        >
          {group.checklistItemName}
          <Text as="span" color="compliance.text.muted" fontWeight="normal">
            {/* Non-breaking space before the separator so a wrap can only fall
                after it. A line that opens with "- " reads as a stray bullet. */}
            {'\u00a0- '}
            {formatPlatformRequirementProblemType(group.problemType)}
          </Text>
        </Text>
        {showStatusChip && displayStatus != null ? (
          <ChecklistIssueStatusChip status={displayStatus} />
        ) : null}
      </Flex>
    </ChecklistRowDisclosure>
  );
};

/**
 * Agent compliance review row. Two lines inside the suggestions card, matching
 * `DocumentSuggestionsList`: the reason reads as the headline and the checklist
 * item takes the meta slot a validation suggestion uses for its page label,
 * alongside the Ignore/Undo affordance.
 */
const SuggestionChecklistRow: FC<{
  group: ChecklistGroup;
  state: ChecklistGroupState;
  showStatusChip: boolean;
  actions?: ChecklistIssueActions;
}> = ({ group, state, showStatusChip, actions }) => {
  const {
    isResolved,
    displayStatus,
    ignoreReasons,
    openIssues,
    ignoredIssues,
  } = state;
  const pendingAction = actions?.pendingAction;
  const rowPendingAction = pendingAction?.publicIssueIds.some((publicIssueId) =>
    group.issues.some((issue) => issue.publicIssueId === publicIssueId)
  )
    ? pendingAction.action
    : null;

  // stopPropagation so the affordance doesn't also trigger any row-level
  // handler the card adds later.
  const handleIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onIgnore(openIssues);
    }
  };
  const handleUndoIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onUndoIgnore(ignoredIssues);
    }
  };

  const canIgnore = actions != null && openIssues.length > 0;
  const canUndo = actions != null && ignoredIssues.length > 0;

  return (
    <ChecklistRowDisclosure group={group} ignoreReasons={ignoreReasons}>
      <Flex
        align="flex-start"
        borderRadius="6px"
        gap={2}
        mx={-2}
        opacity={isResolved ? 0.6 : undefined}
        px={2}
        py={1}
      >
        <Icon
          as={TriangleAlertIcon}
          boxSize="16px"
          color={
            isResolved ? 'compliance.text.muted' : 'compliance.status.warning'
          }
          flexShrink={0}
          mt={1}
        />
        <Flex direction="column" gap={1} minW={0}>
          <Flex align="center" gap={2}>
            <Text
              color={
                isResolved ? 'compliance.text.muted' : 'compliance.text.primary'
              }
              fontSize="sm"
              textDecoration={isResolved ? 'line-through' : undefined}
            >
              {formatPlatformRequirementProblemType(group.problemType)}
            </Text>
            {showStatusChip && displayStatus != null ? (
              <ChecklistIssueStatusChip status={displayStatus} />
            ) : null}
          </Flex>
          {/* Semantic token rather than the `whiteAlpha.600` its sibling
            DocumentSuggestionsList uses: that list is agent-page-only, but this
            component is shared and has to survive the internal light theme.
            Wraps, too, so a long checklist item pushes "• Ignore" onto the next
            line instead of leaving it floating beside a wrapped block — item
            names here are far longer than the page label the validation
            counterpart shows, which never needed to wrap. */}
          <Flex
            align="center"
            color="compliance.text.muted"
            fontSize="sm"
            gap={1.5}
            wrap="wrap"
          >
            <Text>{group.checklistItemName}</Text>
            {rowPendingAction != null ? (
              <>
                <Text aria-hidden>•</Text>
                <Flex align="center" gap={1.5}>
                  <Spinner size="xs" />
                  <Text>
                    {rowPendingAction === 'undo' ? 'Undoing' : 'Ignoring'}
                  </Text>
                </Flex>
              </>
            ) : null}
            {actions && !actions.isIgnoring && canIgnore ? (
              <>
                <Text aria-hidden>•</Text>
                <Text
                  _hover={{ color: 'compliance.text.secondary' }}
                  aria-label="Ignore checklist issue"
                  as="button"
                  cursor="pointer"
                  textDecoration="underline"
                  type="button"
                  onClick={handleIgnore}
                >
                  Ignore
                </Text>
              </>
            ) : null}
            {actions && !actions.isIgnoring && canUndo ? (
              <>
                <Text aria-hidden>•</Text>
                <Text
                  _hover={{ color: 'compliance.text.secondary' }}
                  aria-label="Undo ignore checklist issue"
                  as="button"
                  cursor="pointer"
                  textDecoration="underline"
                  type="button"
                  onClick={handleUndoIgnore}
                >
                  Undo
                </Text>
              </>
            ) : null}
          </Flex>
        </Flex>
      </Flex>
    </ChecklistRowDisclosure>
  );
};

/**
 * The internal debug drawer's full evaluation card: QA section, checklist item,
 * the requirement box, and one card per evaluated attachment.
 */
const ExpandedChecklistGroup: FC<{
  group: ChecklistGroup;
  state: ChecklistGroupState;
  evaluationStatus: ChecklistEvaluationStatus;
  selectedIngestionDocumentId?: string;
  selectedPlatformDocumentId?: string | null;
  showEvaluatedAt: boolean;
  showStatusChip: boolean;
  actions?: ChecklistIssueActions;
  onOpenDocument?: (document: ChecklistEvaluationDocumentView) => void;
}> = ({
  group,
  state,
  evaluationStatus,
  selectedIngestionDocumentId,
  selectedPlatformDocumentId,
  showEvaluatedAt,
  showStatusChip,
  actions,
  onOpenDocument,
}) => {
  const {
    isResolved,
    displayStatus,
    ignoreReasons,
    openIssues,
    ignoredIssues,
  } = state;
  const pendingAction = actions?.pendingAction;
  const rowPendingAction = pendingAction?.publicIssueIds.some((publicIssueId) =>
    group.issues.some((issue) => issue.publicIssueId === publicIssueId)
  )
    ? pendingAction.action
    : null;
  // Applied per-element rather than to the whole group: CSS opacity fades an
  // entire subtree, and a child cannot exceed its parent's value, so a
  // group-level fade would drag the status chip down with it. The chip has to
  // stay legible — it is the only thing saying who handled this.
  const fadedOpacity = isResolved ? 0.6 : 1;
  const mismatchCount = group.evaluationDocuments.filter(
    (document) => document.isProblemDocument
  ).length;
  const formattedEvaluatedAt = formatChecklistTimestamp(group.evaluatedAt);

  const handleIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onIgnore(openIssues);
    }
  };
  const handleUndoIgnore = (event: MouseEvent): void => {
    event.stopPropagation();
    if (actions && !actions.isIgnoring) {
      actions.onUndoIgnore(ignoredIssues);
    }
  };

  return (
    <VStack align="stretch" spacing={3}>
      {group.checklistSectionName ? (
        <Box aria-label="QA section" opacity={fadedOpacity} role="group">
          <Label>QA Section</Label>
          <Text
            color="compliance.text.primary"
            fontSize="sm"
            fontWeight="semibold"
          >
            {group.checklistSectionName}
          </Text>
        </Box>
      ) : null}

      <Box>
        <Label opacity={fadedOpacity}>Checklist Item</Label>
        <Flex align="center" gap={2}>
          <Tooltip
            hasArrow
            bg="compliance.tooltip.bg"
            isDisabled={!isResolved || ignoreReasons.length === 0}
            label={
              ignoreReasons.length > 0 ? (
                <IgnoreReasons reasons={ignoreReasons} />
              ) : undefined
            }
            variant="muted"
          >
            <Text
              color="compliance.text.primary"
              fontSize="sm"
              fontWeight="semibold"
              opacity={fadedOpacity}
              textDecoration={isResolved ? 'line-through' : undefined}
            >
              {group.checklistItemName}
            </Text>
          </Tooltip>
          {showStatusChip && displayStatus != null ? (
            <ChecklistIssueStatusChip status={displayStatus} />
          ) : null}
        </Flex>
        {showEvaluatedAt && formattedEvaluatedAt ? (
          <Text
            color="compliance.text.muted"
            fontSize="xs"
            opacity={fadedOpacity}
          >
            Evaluated {formattedEvaluatedAt}
          </Text>
        ) : null}
      </Box>

      <VStack align="stretch" opacity={fadedOpacity} spacing={3}>
        <RequiredClassificationBox
          allowedDocumentCategories={group.allowedDocumentCategories}
          allowedForms={group.allowedForms}
        />

        <Box>
          <Label>Documents checked</Label>
          <Text color="compliance.text.muted" fontSize="xs">
            {mismatchCount} {mismatchCount === 1 ? 'mismatch' : 'mismatches'}
          </Text>
        </Box>

        <VStack align="stretch" spacing={2}>
          {group.evaluationDocuments.map((document) => {
            const isSelected =
              (document.ingestionDocumentId != null &&
                document.ingestionDocumentId === selectedIngestionDocumentId) ||
              (document.platformDocumentId != null &&
                document.platformDocumentId === selectedPlatformDocumentId);

            return (
              <AttachedDocumentCard
                key={document.renderKey}
                contractDocuments={document.contractDocuments}
                documentName={document.documentName}
                evaluationStatus={evaluationStatus}
                isProblemDocument={document.isProblemDocument}
                isSelected={isSelected}
                verdict={document.verdict}
                onOpen={
                  onOpenDocument ? () => onOpenDocument(document) : undefined
                }
              />
            );
          })}
        </VStack>

        {rowPendingAction != null ? (
          <Flex
            align="center"
            color="compliance.text.muted"
            fontSize="sm"
            gap={2}
          >
            <Spinner size="xs" />
            {rowPendingAction === 'undo' ? 'Undoing' : 'Ignoring'}
          </Flex>
        ) : null}
        {actions && !actions.isIgnoring && openIssues.length > 0 ? (
          <Text
            aria-label="Ignore checklist issue"
            as="button"
            color="compliance.text.muted"
            fontSize="sm"
            textAlign="left"
            textDecoration="underline"
            type="button"
            onClick={handleIgnore}
          >
            Ignore
          </Text>
        ) : null}
        {actions && !actions.isIgnoring && ignoredIssues.length > 0 ? (
          <Text
            aria-label="Undo ignore checklist issue"
            as="button"
            color="compliance.text.muted"
            fontSize="sm"
            textAlign="left"
            textDecoration="underline"
            type="button"
            onClick={handleUndoIgnore}
          >
            Undo
          </Text>
        ) : null}
      </VStack>
    </VStack>
  );
};

/**
 * Read-only checklist mismatch list shared across the internal (light) and
 * agent/external (dark) compliance surfaces. Themed via `compliance.*` tokens.
 * The optional `actions` branch adds the agent-only Ignore/Undo affordances.
 *
 * `rowVariant` picks how each group is drawn — see
 * {@link ChecklistIssueRowVariant}.
 */
export const ChecklistIssuesList: FC<ChecklistIssuesListProps> = (props) => {
  const {
    issues,
    selectedIngestionDocumentId,
    selectedPlatformDocumentId,
    evaluationStatus,
    rowVariant = 'expanded',
    checklistItemName,
    requiredDocumentCategories = [],
    requiredForms = [],
    contextDocuments = [],
    showEvaluatedAt = false,
    showStatusChip = false,
    onOpenDocument,
  } = props;
  const actions = 'actions' in props ? props.actions : undefined;
  const isExpanded = rowVariant === 'expanded';

  if (evaluationStatus === 'unavailable') {
    return (
      <Text color="compliance.text.muted" fontSize="sm">
        Checklist issues are unavailable.
      </Text>
    );
  }

  if (evaluationStatus === 'in_progress') {
    return (
      <Flex align="center" gap={2}>
        <Spinner size="sm" />
        <Text color="compliance.text.muted" fontSize="sm">
          Evaluating…
        </Text>
      </Flex>
    );
  }

  if (issues.length === 0) {
    // Agent-facing surfaces show nothing rather than an empty shell. Without a
    // mismatch there is no finding to report, and "No evaluation data yet" only
    // ever raised questions for the reader (BLO-13465). The internal drawer
    // keeps the honest no-data state, where it is a debugging signal.
    if (
      !isExpanded ||
      (contextDocuments.length === 0 && checklistItemName == null)
    ) {
      return null;
    }

    return (
      <VStack align="stretch" spacing={3}>
        <Text color="compliance.text.muted" fontSize="sm">
          {evaluationStatus === 'evaluated'
            ? 'No mismatches'
            : 'No evaluation data yet'}
        </Text>
        {checklistItemName != null ? (
          <Box>
            <Label>Checklist Item</Label>
            <Text
              color="compliance.text.primary"
              fontSize="sm"
              fontWeight="semibold"
            >
              {checklistItemName}
            </Text>
          </Box>
        ) : null}
        <RequiredClassificationBox
          allowedDocumentCategories={requiredDocumentCategories}
          allowedForms={requiredForms}
        />
        {contextDocuments.length > 0 ? (
          <Box>
            <Label>Documents attached to this item</Label>
            <Text color="compliance.text.muted" fontSize="xs">
              {contextDocuments.length} attached
              {evaluationStatus === 'evaluated'
                ? ` · ${
                    contextDocuments.filter(
                      (document) =>
                        document.hasCurrentProblem ||
                        document.verdict === 'MISMATCHED'
                    ).length
                  } mismatch`
                : ''}
            </Text>
            <VStack align="stretch" mt={1} spacing={2}>
              {contextDocuments.map((document) => {
                const isSelected =
                  document.id === selectedIngestionDocumentId ||
                  document.id === selectedPlatformDocumentId;

                return (
                  <AttachedDocumentCard
                    key={document.id}
                    contractDocuments={document.contractDocuments}
                    documentName={document.documentName}
                    evaluationStatus={evaluationStatus}
                    isProblemDocument={document.hasCurrentProblem}
                    isSelected={isSelected}
                    verdict={document.verdict}
                  />
                );
              })}
            </VStack>
          </Box>
        ) : null}
      </VStack>
    );
  }

  const groups = groupChecklistIssues(issues);

  const renderGroup = (group: ChecklistGroup): ReactNode => {
    const state = deriveGroupState(group);

    return match(rowVariant)
      .with('compact', () => (
        <CompactChecklistRow
          group={group}
          showStatusChip={showStatusChip}
          state={state}
        />
      ))
      .with('suggestion', () => (
        <SuggestionChecklistRow
          actions={actions}
          group={group}
          showStatusChip={showStatusChip}
          state={state}
        />
      ))
      .with('expanded', () => (
        <ExpandedChecklistGroup
          actions={actions}
          evaluationStatus={evaluationStatus}
          group={group}
          selectedIngestionDocumentId={selectedIngestionDocumentId}
          selectedPlatformDocumentId={selectedPlatformDocumentId}
          showEvaluatedAt={showEvaluatedAt}
          showStatusChip={showStatusChip}
          state={state}
          onOpenDocument={onOpenDocument}
        />
      ))
      .exhaustive();
  };

  return (
    <VStack align="stretch" spacing={isExpanded ? 4 : 1}>
      {groups.map((group) => (
        <Box key={group.renderGroupKey}>{renderGroup(group)}</Box>
      ))}
    </VStack>
  );
};
